import { useState, useEffect } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { AlertCircle, CheckCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { getApiUrl } from '../../utils/api';

const Shell = ({ children }) => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary to-primary-dark p-4">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-modal p-8 md:p-10">
            <div className="text-center mb-6">
                <img src="/nhclogo.png" alt="National HealthClaim" className="h-16 w-auto mx-auto" />
                <div className="text-xs font-semibold tracking-widest text-gray-500 mt-2 uppercase">Sales Portal</div>
            </div>
            {children}
            <Link to="/signin" className="block mt-4 text-center text-sm text-primary hover:text-primary-dark transition-colors">
                Return to login
            </Link>
        </div>
    </div>
);

const SetPasswordPage = () => {
    const { token } = useParams();
    const history = useHistory();
    const [tokenState, setTokenState] = useState('checking');
    const [form, setForm] = useState({ password: '', confirmPassword: '' });
    const [show, setShow] = useState({ password: false, confirmPassword: false });
    const [message, setMessage] = useState(null);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await fetch(`${getApiUrl()}/users/validatetoken/${token}`);
                const data = await res.json().catch(() => ({}));
                if (active) setTokenState(data.success ? 'valid' : 'invalid');
            } catch {
                if (active) setTokenState('invalid');
            }
        })();
        return () => { active = false; };
    }, [token]);

    const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setMessage(null); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);
        try {
            const res = await fetch(`${getApiUrl()}/users/setpassword`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, passwordToken: token }),
            });
            const data = await res.json().catch(() => ({}));
            if (data.success) {
                setSuccess(true);
                setMessage('Password has been successfully set. Please login.');
                setTimeout(() => history.push('/signin'), 1500);
                return;
            }
            setSuccess(false);
            setMessage(data.message || 'Something went wrong. Please try again.');
        } catch {
            setSuccess(false);
            setMessage('Unable to connect. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    if (tokenState === 'checking') {
        return (
            <Shell>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-4">
                    <Loader2 className="w-5 h-5 animate-spin" />Checking your link...
                </div>
            </Shell>
        );
    }

    if (tokenState === 'invalid') {
        return (
            <Shell>
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>This password reset link has expired or is invalid. Please request a new one.</span>
                </div>
                <Link to="/resetpassword" className="block mt-4 text-center text-sm font-medium text-primary hover:text-primary-dark transition-colors">
                    Request a new link
                </Link>
            </Shell>
        );
    }

    const field = (name, label, placeholder) => (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
            <div className="relative">
                <input type={show[name] ? 'text' : 'password'} name={name} value={form[name]}
                    onChange={(e) => set(name, e.target.value)} placeholder={placeholder} required
                    className="w-full p-4 pr-12 border border-gray-200 rounded-xl text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all duration-200" />
                <button type="button" aria-label={show[name] ? 'Hide password' : 'Show password'}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    onMouseDown={() => setShow(p => ({ ...p, [name]: true }))}
                    onMouseUp={() => setShow(p => ({ ...p, [name]: false }))}
                    onMouseLeave={() => setShow(p => ({ ...p, [name]: false }))}>
                    {show[name] ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
            </div>
        </div>
    );

    return (
        <Shell>
            <h2 className="text-center text-lg font-semibold text-gray-900 mb-6">Enter your new password</h2>

            {message && success && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" /><span>{message}</span>
                </div>
            )}

            {message && !success && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{message}</span>
                </div>
            )}

            <form onSubmit={handleSubmit}>
                {field('password', 'Password', 'At least 7 characters, with a number')}
                {field('confirmPassword', 'Confirm Password', 'Confirm your password')}
                <button type="submit" disabled={loading || success}
                    className="w-full bg-primary text-white py-4 px-6 rounded-full font-semibold text-base shadow-sm hover:bg-primary-dark active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                    {loading ? 'Submitting...' : 'Submit'}
                </button>
            </form>
        </Shell>
    );
};

export default SetPasswordPage;
